/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_COSTUME_MATRIX_REPORT } from "../graphql";
import { VApplication } from "../../../application";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IInventoryMethods extends IPluginMethodMap {
  getInventoryProps(args: any): Promise<any>;
}

export class InventoryService implements IAgentPlugin {
  readonly methods: IInventoryMethods;

  constructor() {
    this.methods = {
      getInventoryProps: this.getInventoryProps.bind(this),
    };
  }

  async getInventoryProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    console.log("matrix args->", args);
    const storageSrv = VApplication.getInstance().getSync("services.StorageService") as any;
    let _sessionToken = storageSrv?.get("userProfile", "session")?.accessToken;
    let _localToken = storageSrv?.get("userProfile", "local")?.accessToken;
    let _token = _localToken ?? _sessionToken;
    console.log("matrix service token ->", _token);

    const baseFilter: any = {
      presentationId: args?.presentationId || args?.id,
      name: {
        contains: args?.commonSearch || args?.name?.contains || "",
      },
    };

    const options = {
      limit: args?.size ?? args?.limit ?? 100,
      skip: args?.offset ?? 0,
    };

    try {
      if (!baseFilter.presentationId) {
        return {
          ultUsers: [],
          costumeTypes: [],
          pageSize: 0,
          totalCount: 0,
          isEmpty: true,
        };
      }

      console.log("MATRIX QUERY - baseFilter:", JSON.stringify(baseFilter));
      console.log("MATRIX QUERY - options:", JSON.stringify(options));
      console.log("MATRIX QUERY - inventLimit filter:", JSON.stringify({
        ...baseFilter,
        inventoryType: "inventLimit",
      }));

      const [limitRes, registeredRes, remainingRes] = await Promise.all([
        client.query({
          query: GET_COSTUME_MATRIX_REPORT,
          variables: {
            filter: {
              ...baseFilter,
              inventoryType: "inventLimit",
            },
            options,
          },
          fetchPolicy: "network-only",
          errorPolicy: "all",
        }),
        client.query({
          query: GET_COSTUME_MATRIX_REPORT,
          variables: {
            filter: {
              ...baseFilter,
              inventoryType: "registered",
            },
            options,
          },
          fetchPolicy: "network-only",
          errorPolicy: "all",
        }),
        client.query({
          query: GET_COSTUME_MATRIX_REPORT,
          variables: {
            filter: {
              ...baseFilter,
              inventoryType: "remaining",
            },
            options,
          },
          fetchPolicy: "network-only",
          errorPolicy: "all",
        }),
      ]);

      const limitContent = limitRes?.data?.getSectionCostumeTypeInventoryMatrixReport?.content || [];
      const registeredContent = registeredRes?.data?.getSectionCostumeTypeInventoryMatrixReport?.content || [];
      const remainingContent = remainingRes?.data?.getSectionCostumeTypeInventoryMatrixReport?.content || [];

      console.log("limitRes data content =>", limitContent);
      console.log("registeredRes data content =>", registeredContent);
      console.log("remainingRes data content =>", remainingContent);

      if (limitRes?.errors) console.error("limitRes GraphQL errors =>", limitRes.errors);
      if (registeredRes?.errors) console.error("registeredRes GraphQL errors =>", registeredRes.errors);
      if (remainingRes?.errors) console.error("remainingRes GraphQL errors =>", remainingRes.errors);

      // Extract unique costume types list to dynamic columns
      const costumeTypesMap: { [id: string]: string } = {};
      const extractCostumeTypes = (content: any[]) => {
        content.forEach((item) => {
          (item.costumeTypes || []).forEach((ct: any) => {
            if (ct.costumeTypeId) {
              costumeTypesMap[ct.costumeTypeId] = ct.costumeTypeName;
            }
          });
        });
      };
      extractCostumeTypes(limitContent);
      extractCostumeTypes(registeredContent);
      extractCostumeTypes(remainingContent);

      const costumeTypesList = Object.keys(costumeTypesMap).map((id) => ({
        id,
        name: costumeTypesMap[id],
      }));

      // Group section data
      const sectionsMap: { [sectionId: string]: any } = {};

      const getOrCreateSection = (sectionId: string, sectionName: string) => {
        if (!sectionsMap[sectionId]) {
          sectionsMap[sectionId] = {
            sectionId,
            sectionName,
            inventLimit: { costumeTypes: {}, total: 0 },
            registered: { costumeTypes: {}, total: 0 },
            remaining: { costumeTypes: {}, total: 0 },
          };
        }
        return sectionsMap[sectionId];
      };

      limitContent.forEach((item: any) => {
        const sec = getOrCreateSection(item.sectionId, item.sectionName);
        sec.inventLimit.total = item.total ?? 0;
        (item.costumeTypes || []).forEach((ct: any) => {
          sec.inventLimit.costumeTypes[ct.costumeTypeId] = ct.value ?? 0;
        });
      });

      registeredContent.forEach((item: any) => {
        const sec = getOrCreateSection(item.sectionId, item.sectionName);
        sec.registered.total = item.total ?? 0;
        (item.costumeTypes || []).forEach((ct: any) => {
          sec.registered.costumeTypes[ct.costumeTypeId] = ct.value ?? 0;
        });
      });

      remainingContent.forEach((item: any) => {
        const sec = getOrCreateSection(item.sectionId, item.sectionName);
        sec.remaining.total = item.total ?? 0;
        (item.costumeTypes || []).forEach((ct: any) => {
          sec.remaining.costumeTypes[ct.costumeTypeId] = ct.value ?? 0;
        });
      });

      const matrixItems = Object.values(sectionsMap);
      const count = limitRes?.data?.getSectionCostumeTypeInventoryMatrixReport?.count ?? matrixItems.length;

      return {
        ultUsers: matrixItems,
        costumeTypes: costumeTypesList,
        pageSize: count,
        totalCount: count,
        isEmpty: matrixItems.length === 0,
      };
    } catch (err: any) {
      console.log("error fetching matrix report in InventoryService", err);
    }

    return {
      ultUsers: [],
      costumeTypes: [],
      pageSize: 0,
      totalCount: 0,
      isEmpty: true,
    };
  }
}
